import type { RGB } from '../types';
import { rgbToHsl, hslToRgb, rgbToHex } from './colors';
import { generateTints, generateShades } from './colorAnalysis';

export type Mood = 'professional' | 'playful' | 'luxurious' | 'natural' | 'tech' | 'bold';
export type Industry = 'healthcare' | 'finance' | 'food' | 'technology' | 'fashion' | 'education';

export interface BrandPalette {
  primary: string;
  secondary: string;
  accent: string;
  neutral: string;
  success: string;
  warning: string;
  danger: string;
  background: string;
  surface: string;
  text: string;
  tints: string[];
  shades: string[];
}

export const MOOD_LABELS: Record<Mood, string> = {
  professional: 'Professional',
  playful: 'Playful',
  luxurious: 'Luxurious',
  natural: 'Natural',
  tech: 'Tech / Modern',
  bold: 'Bold / Energetic',
};

export const INDUSTRY_LABELS: Record<Industry, string> = {
  healthcare: 'Healthcare',
  finance: 'Finance',
  food: 'Food & Beverage',
  technology: 'Technology',
  fashion: 'Fashion',
  education: 'Education',
};

// Mood defines how we derive secondary/accent colors
interface MoodConfig {
  hueShift: number;        // secondary hue offset from primary
  accentHueShift: number;  // accent hue offset
  satAdjust: number;       // saturation adjustment (-20 to +20)
  lightAdjust: number;     // lightness adjustment
}

const MOOD_CONFIG: Record<Mood, MoodConfig> = {
  professional: { hueShift: 30, accentHueShift: 180, satAdjust: -15, lightAdjust: 0 },
  playful:      { hueShift: 60, accentHueShift: 120, satAdjust: +10, lightAdjust: +5 },
  luxurious:    { hueShift: 20, accentHueShift: 40, satAdjust: -10, lightAdjust: -10 },
  natural:      { hueShift: 40, accentHueShift: 80, satAdjust: -20, lightAdjust: +5 },
  tech:         { hueShift: 180, accentHueShift: 210, satAdjust: +5, lightAdjust: 0 },
  bold:         { hueShift: 150, accentHueShift: 60, satAdjust: +15, lightAdjust: -5 },
};

// Industry provides constraints on hue ranges
interface IndustryConstraint {
  preferredSecondaryHueRange: [number, number]; // preferred hue range for secondary
  maxSaturation: number;
  minLightness: number;
}

const INDUSTRY_CONSTRAINTS: Record<Industry, IndustryConstraint> = {
  healthcare:  { preferredSecondaryHueRange: [160, 220], maxSaturation: 70, minLightness: 40 },
  finance:     { preferredSecondaryHueRange: [200, 260], maxSaturation: 65, minLightness: 35 },
  food:        { preferredSecondaryHueRange: [20, 80], maxSaturation: 85, minLightness: 45 },
  technology:  { preferredSecondaryHueRange: [180, 280], maxSaturation: 90, minLightness: 30 },
  fashion:     { preferredSecondaryHueRange: [280, 360], maxSaturation: 80, minLightness: 35 },
  education:   { preferredSecondaryHueRange: [190, 250], maxSaturation: 75, minLightness: 40 },
};

const clamp = (v: number, min: number, max: number) => Math.min(Math.max(v, min), max);

const makeHex = (h: number, s: number, l: number): string => {
  const rgb = hslToRgb(((h % 360) + 360) % 360, clamp(s, 5, 100), clamp(l, 10, 95));
  return rgbToHex(rgb.r, rgb.g, rgb.b);
};

export const generateBrandPalette = (
  primaryRgb: RGB,
  mood: Mood,
  industry: Industry,
): BrandPalette => {
  const hsl = rgbToHsl(primaryRgb.r, primaryRgb.g, primaryRgb.b);
  const mc = MOOD_CONFIG[mood];
  const ic = INDUSTRY_CONSTRAINTS[industry];

  const primary = rgbToHex(primaryRgb.r, primaryRgb.g, primaryRgb.b);

  // Secondary: mood-driven hue shift, constrained by industry
  let secHue = (hsl.h + mc.hueShift) % 360;
  const [loHue, hiHue] = ic.preferredSecondaryHueRange;
  // Nudge toward industry range if far away
  const distToRange = secHue < loHue ? loHue - secHue : secHue > hiHue ? secHue - hiHue : 0;
  if (distToRange > 60) {
    secHue = (loHue + hiHue) / 2; // snap to center of industry range
  }
  const secSat = clamp(hsl.s + mc.satAdjust, 20, ic.maxSaturation);
  const secLight = clamp(hsl.l + mc.lightAdjust, ic.minLightness, 70);
  const secondary = makeHex(secHue, secSat, secLight);

  // Accent: mood-driven, high saturation
  const accHue = (hsl.h + mc.accentHueShift) % 360;
  const accent = makeHex(accHue, clamp(hsl.s + 10, 50, 95), clamp(hsl.l, 40, 60));

  // Neutral: desaturated version of primary
  const neutral = makeHex(hsl.h, 8, 50);

  // Semantic colors
  const success = makeHex(145, 65, 42);
  const warning = makeHex(42, 90, 50);
  const danger = makeHex(4, 80, 48);

  // Surface / Background / Text
  const background = makeHex(hsl.h, clamp(hsl.s, 3, 12), 97);
  const surface = makeHex(hsl.h, clamp(hsl.s, 2, 8), 100);
  const text = makeHex(hsl.h, clamp(hsl.s, 5, 15), 12);

  return {
    primary,
    secondary,
    accent,
    neutral,
    success,
    warning,
    danger,
    background,
    surface,
    text,
    tints: generateTints(primary, 9),
    shades: generateShades(primary, 9),
  };
};
